import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { TOOL_NAMES } from 'js/src/agent/toolNames';

import { sceneDisplayId } from '../tools/scene-addressing';
import { canvasSizeOf, measureElements, ownElementText } from './lint/measure';
import type { FrameSource, Rect } from './lint/measure';
import type { LintFinding } from './lint';

import type { SceneHandoff } from 'js/src/data/html-design';
import {
    hasAppliedMatchCutWithNextScene,
    hasAppliedTransition,
} from 'js/src/luminary/luminary-selectors';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';

/**
 * Checks the seams a video carries an element across: the element a graphic leaves on screen at
 * its last frame has to be the element the next graphic opens on, in the same pose, or the "carry"
 * is a jump cut with extra steps.
 *
 * Driven by the `data-l5-carry` marks in the compositions themselves rather than by what the build
 * stored, so a seam is still checked in a later session, after a reload has emptied the store.
 * The neighbour is measured live for the same reason: a stored pose goes stale the moment the
 * neighbour is re-timed or edited.
 */

/** What a carried element looks like at one frame, in canvas pixels. */
export interface CarryPose {
    rect: Rect;
    opacity: number;
    /** How its copy is set; absent for a carried shape with no copy, whose font means nothing. */
    type?: { fontSizePx: number; color: string };
    /** The rendered pixels inside `rect`, RGBA; null when the frame could not be rasterized. */
    pixels: {
        data: Uint8ClampedArray;
        width: number;
        height: number;
        /** Where the crop starts on the canvas, which is clamped to it for a part off its edge. */
        x?: number;
        y?: number;
    } | null;
    /** Whether the element and its immediate surround hold nothing but background. */
    blank: boolean;
    /**
     * The flat colour the element sits on at this frame, read from just outside it, when one colour
     * holds most of that surround. Absent when the surround is busy.
     */
    ground?: [number, number, number];
    /** Whether nothing at all renders on the frame, read only where the element is blank. */
    frameBlank?: boolean;
    /**
     * What else this frame draws over the element's area, in canvas pixels: each graphic's own
     * headline and labels, which a large carried element spans but which are not part of it.
     */
    others?: Rect[];
    /**
     * The copied-in element inside a wrapper that hands it on with what this graphic adds — what
     * the seam into this graphic is judged by, since the additions are new here.
     */
    opensAs?: CarryPose;
    /** The copy each text leaf inside it renders at this frame, in document order. */
    texts: string[];
    /**
     * The element as it stands at this frame, placed on the canvas in plain CSS, and the stylesheet
     * rules it is drawn by — what a graphic carrying it in is spliced from — or why it can't be.
     * Only taken on request.
     */
    copy?: CarriedCopy | CopyRefusal;
}

export interface CarriedCopy {
    element: string;
    rules: string[];
}

/**
 * Why an element can't be copied in as it stands. `fix` is for the graphic that marks it; the one
 * that would have copied it has to rebuild it instead.
 */
export type CopyRefusal = { refused: string; fix: string };

/** At a hard cut every pixel of drift is visible, and the author is handed the exact numbers. */
const POSITION_TOLERANCE_PX = 4;
const SIZE_TOLERANCE_RATIO = 0.02;
const FONT_SIZE_TOLERANCE_PX = 1;
/** A carry faded partway is an element arriving, not one already there. */
const VISIBLE_OPACITY = 0.95;
/**
 * A pixel counts as different when a channel moves this far — well clear of the anti-aliasing a
 * sub-pixel offset produces along an edge.
 */
const PIXEL_CHANNEL_DELTA = 64;
/**
 * The share of differing pixels that reads as "drawn differently". A stroke still undrawn, a fill
 * that changed colour or a weight that changed all clear it by far; a glyph edge landing a pixel
 * over does not.
 */
const PIXEL_DIFFERENCE_SHARE = 0.03;

/** An element may carry one thing in and hand another on, so the attribute is a list of ids. */
function carryIdsOf(value: string | null | undefined): string[] {
    return (value ?? '').split(/\s+/).filter(Boolean);
}

/**
 * Whether an element sits inside another marked with the same id: a wrapper handing on a copy
 * together with what its graphic adds, which is one carry, measured and copied as the wrapper.
 */
function nestedInCarry(element: Element, id: string): boolean {
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
        if (carryIdsOf(parent.getAttribute('data-l5-carry')).includes(id)) {
            return true;
        }
    }
    return false;
}

/**
 * The carry ids a composition marks, read from its markup so no composition has to be loaded. Parsed
 * rather than pattern-matched, so a `[data-l5-carry="bar"]` selector in a stylesheet or a script is
 * not taken for an element that carries it.
 */
export function markedCarryIds(html: string | null | undefined): Set<string> {
    if (!html || !html.includes('data-l5-carry')) {
        return new Set();
    }
    const document = new DOMParser().parseFromString(html, 'text/html');
    return new Set(
        Array.from(document.querySelectorAll('[data-l5-carry]')).flatMap((element) =>
            carryIdsOf(element.getAttribute('data-l5-carry')),
        ),
    );
}

/**
 * The ids this composition marks that the graphic straight before it marks too — what it opens on,
 * as opposed to what it only hands on.
 */
export function incomingCarryIds(luminary: Luminary, scene: Scene, html: string): Set<string> {
    const scenes = luminary.scenes;
    const previous = graphicOf(scenes[scenes.indexOf(scene) - 1]);
    const theirs = markedCarryIds(previous?.html);
    return new Set([...markedCarryIds(html)].filter((id) => theirs.has(id)));
}

/**
 * A channel spread this small across the whole region is a flat field: nothing is drawn there but
 * the background. An empty box marked as the carry is exactly that, and it matches its own twin on
 * the other side of the cut however far apart the things the viewer actually sees are.
 */
const BLANK_CHANNEL_SPREAD = 16;

/**
 * How far past its own box the blank check looks. A solid shape in one flat colour is perfectly
 * uniform inside its own box, so only its edge against the background tells it from an empty one.
 */
const BLANK_SURROUND_PX = 4;

function withSurround(rect: Rect): Rect {
    return {
        x: rect.x - BLANK_SURROUND_PX,
        y: rect.y - BLANK_SURROUND_PX,
        width: rect.width + 2 * BLANK_SURROUND_PX,
        height: rect.height + 2 * BLANK_SURROUND_PX,
    };
}

export function drawsNothing(pixels: CarryPose['pixels']): boolean {
    if (!pixels || pixels.width * pixels.height === 0) {
        return false;
    }
    for (let channel = 0; channel < 3; channel++) {
        let low = 255;
        let high = 0;
        for (let index = channel; index < pixels.data.length; index += 4) {
            low = Math.min(low, pixels.data[index]);
            high = Math.max(high, pixels.data[index]);
        }
        if (high - low > BLANK_CHANNEL_SPREAD) {
            return false;
        }
    }
    return true;
}

/** The share of the surround one colour has to hold before it is read as the ground. */
const GROUND_SHARE = 0.6;

function nearColour(data: ArrayLike<number>, index: number, colour: readonly number[]): boolean {
    for (let channel = 0; channel < 3; channel++) {
        if (Math.abs(data[index + channel] - colour[channel]) > PIXEL_CHANNEL_DELTA) {
            return false;
        }
    }
    return true;
}

function median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
}

/** The colour that holds most of the band just outside `rect`, if one does. */
function groundAround(
    canvas: HTMLCanvasElement,
    rect: Rect,
    canvasWidth: number,
): CarryPose['ground'] {
    const surround = cropPixels(canvas, withSurround(rect), canvasWidth);
    if (!surround) {
        return undefined;
    }
    const scale = canvas.width / canvasWidth;
    const insideLeft = (rect.x - (surround.x ?? 0)) * scale;
    const insideTop = (rect.y - (surround.y ?? 0)) * scale;
    const insideRight = insideLeft + rect.width * scale;
    const insideBottom = insideTop + rect.height * scale;
    const band: number[] = [];
    for (let row = 0; row < surround.height; row++) {
        for (let column = 0; column < surround.width; column++) {
            if (
                column >= insideLeft &&
                column < insideRight &&
                row >= insideTop &&
                row < insideBottom
            ) {
                continue;
            }
            band.push((row * surround.width + column) * 4);
        }
    }
    if (band.length === 0) {
        return undefined;
    }
    const colour: [number, number, number] = [0, 1, 2].map((channel) =>
        median(band.map((index) => surround.data[index + channel])),
    ) as [number, number, number];
    const matching = band.filter((index) => nearColour(surround.data, index, colour)).length;
    return matching >= GROUND_SHARE * band.length ? colour : undefined;
}

/** A box covering this much of the canvas is a background, which the carry is drawn over. */
const BACKGROUND_SHARE = 0.9;

/**
 * Everything else drawn over a carried element's area: not inside it, not what it sits in, and not
 * a background behind the whole frame. Only what paints by itself — text, and shapes with nothing
 * laid out inside them — since a layout box around a headline covers far more than the headline,
 * and skipping it would leave most of a large carry unchecked.
 */
function otherContentOver(
    element: Element,
    area: Rect,
    measurements: { element: Element; rect: Rect; opacity: number; hasText: boolean }[],
    canvas: { width: number; height: number },
): Rect[] {
    const canvasArea = canvas.width * canvas.height;
    const holdsLaidOutParts = (box: Element): boolean =>
        measurements.some(
            ({ element: part, rect }) =>
                part !== box && box.contains(part) && rect.width > 0 && rect.height > 0,
        );
    return measurements
        .filter(
            ({ element: other, rect, opacity, hasText }) =>
                (hasText || !holdsLaidOutParts(other)) &&
                !element.contains(other) &&
                !other.contains(element) &&
                opacity > 0 &&
                rect.width > 0 &&
                rect.height > 0 &&
                (canvasArea <= 0 || rect.width * rect.height < BACKGROUND_SHARE * canvasArea) &&
                rect.x < area.x + area.width &&
                rect.x + rect.width > area.x &&
                rect.y < area.y + area.height &&
                rect.y + rect.height > area.y,
        )
        .map(({ rect }) => rect);
}

/**
 * Where a carried element and everything inside it draws. A timeline or a dot field is usually a
 * wrapper around absolutely placed parts that sit outside its own box, which on its own can be
 * empty — measured alone it reads as drawing nothing, and two wrappers holding very different
 * parts compare as equal.
 */
function drawnBounds(
    element: Element,
    own: Rect,
    measurements: { element: Element; rect: Rect; opacity: number }[],
): Rect {
    const parts = measurements.filter(
        ({ element: part, rect, opacity }) =>
            element.contains(part) && rect.width > 0 && rect.height > 0 && opacity > 0,
    );
    if (parts.length === 0) {
        return own;
    }
    const left = Math.min(...parts.map(({ rect }) => rect.x));
    const top = Math.min(...parts.map(({ rect }) => rect.y));
    const right = Math.max(...parts.map(({ rect }) => rect.x + rect.width));
    const bottom = Math.max(...parts.map(({ rect }) => rect.y + rect.height));
    return { x: left, y: top, width: right - left, height: bottom - top };
}

/** The carry ids more than one element in the composition is marked with. */
export function carryIdsMarkedTwice(html: string | null | undefined): string[] {
    if (!html || !html.includes('data-l5-carry')) {
        return [];
    }
    const document = new DOMParser().parseFromString(html, 'text/html');
    const counts = new Map<string, number>();
    for (const element of Array.from(document.querySelectorAll('[data-l5-carry]'))) {
        for (const id of carryIdsOf(element.getAttribute('data-l5-carry'))) {
            if (!nestedInCarry(element, id)) {
                counts.set(id, (counts.get(id) ?? 0) + 1);
            }
        }
    }
    return [...counts].filter(([, count]) => count > 1).map(([id]) => id);
}

function round(value: number): number {
    return Math.round(value);
}

export function describePose(pose: CarryPose): string {
    const { x, y, width, height } = pose.rect;
    const box = `left ${round(x)}px, top ${round(y)}px, ${round(width)}×${round(height)}px`;
    return pose.type
        ? `${box}, font-size ${round(pose.type.fontSizePx)}px, colour ${pose.type.color}`
        : box;
}

function sizeDiffers(a: number, b: number): boolean {
    return Math.abs(a - b) > Math.max(POSITION_TOLERANCE_PX, Math.max(a, b) * SIZE_TOLERANCE_RATIO);
}

/**
 * Compared at each element's own origin, over the region both cover, so a pose that passed the
 * geometry check within tolerance doesn't fail here on the offset alone.
 */
function differingPixels(
    a: NonNullable<CarryPose['pixels']>,
    b: NonNullable<CarryPose['pixels']>,
    origin: { x: number; y: number } = { x: 0, y: 0 },
    skip: Rect[] = [],
    grounds: { a?: readonly number[]; b?: readonly number[] } = {},
): { share: number; region: Rect | null } {
    const width = Math.min(a.width, b.width);
    const height = Math.min(a.height, b.height);
    if (width <= 0 || height <= 0) {
        return { share: 0, region: null };
    }
    const skipped = (column: number, row: number): boolean => {
        const x = origin.x + column;
        const y = origin.y + row;
        return skip.some(
            (rect) =>
                x >= rect.x && x < rect.x + rect.width && y >= rect.y && y < rect.y + rect.height,
        );
    };
    let compared = 0;
    let differing = 0;
    let left = width;
    let top = height;
    let right = -1;
    let bottom = -1;
    for (let row = 0; row < height; row++) {
        for (let column = 0; column < width; column++) {
            if (skip.length && skipped(column, row)) {
                continue;
            }
            compared++;
            const ai = (row * a.width + column) * 4;
            const bi = (row * b.width + column) * 4;
            if (
                grounds.a &&
                grounds.b &&
                nearColour(a.data, ai, grounds.a) &&
                nearColour(b.data, bi, grounds.b)
            ) {
                continue;
            }
            for (let channel = 0; channel < 3; channel++) {
                if (Math.abs(a.data[ai + channel] - b.data[bi + channel]) > PIXEL_CHANNEL_DELTA) {
                    differing++;
                    left = Math.min(left, column);
                    top = Math.min(top, row);
                    right = Math.max(right, column);
                    bottom = Math.max(bottom, row);
                    break;
                }
            }
        }
    }
    return {
        share: compared ? differing / compared : 0,
        region:
            right < 0
                ? null
                : { x: left, y: top, width: right - left + 1, height: bottom - top + 1 },
    };
}

/**
 * How the opening pose differs from the closing one, as one sentence per difference, or [] when
 * the seam holds. Geometry first, because it comes with the numbers that fix it; the pixel diff
 * catches what geometry can't see — a stroke revealed by dash offset, a clip or a mask all leave the
 * box at full size while the thing inside it is still undrawn.
 */
export function seamDifferences(closing: CarryPose, opening: CarryPose): string[] {
    const differences: string[] = [];
    const dx = opening.rect.x - closing.rect.x;
    const dy = opening.rect.y - closing.rect.y;
    if (Math.abs(dx) > POSITION_TOLERANCE_PX || Math.abs(dy) > POSITION_TOLERANCE_PX) {
        differences.push(
            `it opens ${round(dx)}px across and ${round(dy)}px down from where it was`,
        );
    }
    if (
        sizeDiffers(closing.rect.width, opening.rect.width) ||
        sizeDiffers(closing.rect.height, opening.rect.height)
    ) {
        differences.push(
            `it opens at ${round(opening.rect.width)}×${round(opening.rect.height)}px, not ` +
                `${round(closing.rect.width)}×${round(closing.rect.height)}px`,
        );
    }
    if (opening.type && closing.type) {
        if (Math.abs(opening.type.fontSizePx - closing.type.fontSizePx) > FONT_SIZE_TOLERANCE_PX) {
            differences.push(
                `its type opens at ${round(opening.type.fontSizePx)}px, not ` +
                    `${round(closing.type.fontSizePx)}px`,
            );
        }
        if (opening.type.color !== closing.type.color) {
            differences.push(
                `its colour opens as ${opening.type.color}, not ${closing.type.color}`,
            );
        }
    }
    if (differences.length === 0 && closing.pixels && opening.pixels) {
        const left = closing.pixels.x ?? closing.rect.x;
        const top = closing.pixels.y ?? closing.rect.y;
        // Each side's own other content is left out: a graphic's headline drawn across a large
        // carried element is not the element changing at the cut.
        const { share, region } = differingPixels(
            closing.pixels,
            opening.pixels,
            { x: left, y: top },
            [...(closing.others ?? []), ...(opening.others ?? [])],
            { a: closing.ground, b: opening.ground },
        );
        if (share > PIXEL_DIFFERENCE_SHARE) {
            // Located on the canvas, because a share alone sends the author hunting through the
            // whole element: the difference is usually one part of it, or something sitting over it.
            const where = region
                ? `, between x ${round(left + region.x)}–` +
                  `${round(left + region.x + region.width)}px and y ` +
                  `${round(top + region.y)}–` +
                  `${round(top + region.y + region.height)}px`
                : '';
            differences.push(
                `${Math.round(share * 100)}% of its pixels are drawn differently${where} — a ` +
                    `stroke still undrawn, a clip or mask not yet open, a changed fill, or something ` +
                    `else sitting over it`,
            );
        }
    }
    return differences;
}

function cropPixels(
    canvas: HTMLCanvasElement,
    rect: Rect,
    canvasWidth: number,
): CarryPose['pixels'] {
    const context = canvas.getContext('2d');
    if (!context || canvasWidth <= 0) {
        return null;
    }
    const scale = canvas.width / canvasWidth;
    const x = Math.max(0, Math.floor(rect.x * scale));
    const y = Math.max(0, Math.floor(rect.y * scale));
    const width = Math.min(canvas.width, Math.ceil((rect.x + rect.width) * scale)) - x;
    const height = Math.min(canvas.height, Math.ceil((rect.y + rect.height) * scale)) - y;
    if (width <= 0 || height <= 0) {
        return null;
    }
    const { data } = context.getImageData(x, y, width, height);
    return { data, width, height, x: x / scale, y: y / scale };
}

/**
 * The poses of every carried element the composition marks, at each requested point of its scene
 * (0..1). An id missing from a frame's map was not rendered there. Null when the composition
 * cannot be loaded here at all (no composition frame in this environment).
 */
export async function measureCarries(
    asset: HtmlAsset,
    progresses: number[],
    { withCopy = false }: { withCopy?: boolean } = {},
): Promise<Map<number, Map<string, CarryPose>> | null> {
    if (markedCarryIds(asset.html).size === 0) {
        return new Map(progresses.map((progress) => [progress, new Map()]));
    }
    return asset.introspectComposition(async (composition) => {
        const byProgress = new Map<number, Map<string, CarryPose>>();
        for (const progress of progresses) {
            // A sandboxed host hands back a posed copy; an older one poses the live document.
            const seek: FrameSource['seek'] = composition.seek;
            const posed = (await seek(progress)) ?? composition.document;
            const measurements = measureElements(posed);
            const { width: canvasWidth, height: canvasHeight } = canvasSizeOf(posed);
            let canvas: HTMLCanvasElement | null = null;
            try {
                canvas = await composition.rasterize(1);
            } catch {
                canvas = null;
            }
            const poses = new Map<string, CarryPose>();
            const openings = new Map<string, CarryPose>();
            let frameBlank: boolean | undefined;
            const wholeFrameBlank = (): boolean => {
                frameBlank ??=
                    !!canvas &&
                    drawsNothing(
                        cropPixels(
                            canvas,
                            { x: 0, y: 0, width: canvasWidth, height: canvas.height },
                            canvasWidth,
                        ),
                    );
                return frameBlank;
            };
            // A copy is found by what it was copied in as, whether or not it still carries the
            // mark: inside a wrapper that hands it on, the mark is the wrapper's to carry.
            for (const element of Array.from(
                posed.querySelectorAll('[data-l5-carry], [data-l5-carried]'),
            )) {
                const root = measurements.find((measurement) => measurement.element === element);
                if (!root) {
                    continue;
                }
                const drawn = drawnBounds(element, root.rect, measurements);
                // Only what shows: a part held hidden for a later graphic is not on screen yet.
                const shownText = measurements.filter(
                    (measurement) =>
                        measurement.hasText &&
                        measurement.opacity > 0 &&
                        element.contains(measurement.element),
                );
                // The type that is carried is usually a leaf inside the marked wrapper.
                const text = shownText[0];
                const pose: CarryPose = {
                    rect: drawn,
                    opacity: root.opacity,
                    ...(text ? { type: { fontSizePx: text.fontSizePx, color: text.color } } : {}),
                    pixels: canvas ? cropPixels(canvas, drawn, canvasWidth) : null,
                    blank:
                        !!canvas &&
                        drawsNothing(cropPixels(canvas, withSurround(drawn), canvasWidth)),
                    texts: shownText
                        .map(({ element: leaf }) => ownElementText(leaf))
                        .filter(Boolean),
                };
                const ground = canvas ? groundAround(canvas, drawn, canvasWidth) : undefined;
                if (ground) {
                    pose.ground = ground;
                }
                if (pose.blank && wholeFrameBlank()) {
                    pose.frameBlank = true;
                }
                const others = otherContentOver(element, drawn, measurements, {
                    width: canvasWidth,
                    height: canvasHeight,
                });
                if (others.length) {
                    pose.others = others;
                }
                const ids = carryIdsOf(element.getAttribute('data-l5-carry'));
                const outermost = ids.filter((id) => !nestedInCarry(element, id));
                const copy = withCopy && outermost.length ? carriedCopy(element, root.rect) : null;
                if (copy) {
                    pose.copy = copy;
                }
                for (const id of outermost) {
                    poses.set(id, pose);
                }
                const copiedInAs = element.getAttribute('data-l5-carried')?.trim();
                if (
                    copiedInAs &&
                    !outermost.includes(copiedInAs) &&
                    nestedInCarry(element, copiedInAs)
                ) {
                    openings.set(copiedInAs, pose);
                }
            }
            for (const [id, opening] of openings) {
                const wrapper = poses.get(id);
                if (wrapper) {
                    poses.set(id, { ...wrapper, opensAs: opening });
                }
            }
            byProgress.set(progress, poses);
        }
        return byProgress;
    });
}

/** The translation in a computed transform's matrix, which the browser reports in px. */
function translationOf(transform: string): { x: number; y: number } {
    const matrix = transform.match(/^matrix\(([^)]+)\)$/);
    if (!matrix) {
        return { x: 0, y: 0 };
    }
    const [, , , , x, y] = matrix[1].split(',').map(Number);
    return { x: x || 0, y: y || 0 };
}

/**
 * The scale a computed transform applies, or null when it also rotates or skews — a pose that plain
 * left/top/width/height plus a scale can't reproduce.
 */
export function pureScale(transform: string): { x: number; y: number } | null {
    if (!transform || transform === 'none') {
        return { x: 1, y: 1 };
    }
    const matrix = transform.match(/^matrix\(([^)]+)\)$/);
    if (!matrix) {
        return null;
    }
    const [a, b, c, d] = matrix[1].split(',').map(Number);
    if (Math.abs(b) > 1e-3 || Math.abs(c) > 1e-3 || !(a > 0) || !(d > 0)) {
        return null;
    }
    return { x: a, y: d };
}

/**
 * The flat rules in a stylesheet's text, as selector and declarations. At-rules are skipped whole: a
 * composition is authored against the timeline, so the ones that matter here (@keyframes, @media
 * for a canvas that never resizes) do not come up, and descending into them would mean parsing CSS
 * properly.
 */
function flatRules(css: string): { selector: string; body: string }[] {
    const rules: { selector: string; body: string }[] = [];
    // Statement at-rules end at a semicolon and have no block, so they are dropped before blocks are
    // read; left in, the rule after one would be read as its body and skipped with it.
    const source = css
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/@(import|charset|namespace|layer)\b[^;{}]*;/gi, '');
    let index = 0;
    while (index < source.length) {
        const open = source.indexOf('{', index);
        if (open < 0) {
            break;
        }
        const selector = source.slice(index, open).trim();
        if (selector.startsWith('@')) {
            let depth = 1;
            let cursor = open + 1;
            while (cursor < source.length && depth > 0) {
                depth += source[cursor] === '{' ? 1 : source[cursor] === '}' ? -1 : 0;
                cursor++;
            }
            index = cursor;
            continue;
        }
        const close = source.indexOf('}', open);
        if (close < 0) {
            break;
        }
        rules.push({ selector, body: source.slice(open + 1, close).trim() });
        index = close + 1;
    }
    return rules;
}

/** A selector list split at its top-level commas, leaving those inside `:is(a, b)` alone. */
function selectorParts(selector: string): string[] {
    const parts: string[] = [];
    let depth = 0;
    let start = 0;
    for (let index = 0; index < selector.length; index++) {
        const character = selector[index];
        depth += character === '(' ? 1 : character === ')' ? -1 : 0;
        if (character === ',' && depth === 0) {
            parts.push(selector.slice(start, index).trim());
            start = index + 1;
        }
    }
    parts.push(selector.slice(start).trim());
    return parts.filter(Boolean);
}

const PSEUDO_ELEMENT =
    /::?(before|after|marker|first-line|first-letter|placeholder|selection)\s*$/i;

/**
 * Selector specificity as [ids, classes, types] — near enough to keep the copied rules in the order
 * they won in. `:where()` counts for nothing; `:is()`, `:not()` and `:has()` count their contents,
 * which is exact for the single-argument forms compositions use.
 */
function specificity(selector: string): [number, number, number] {
    const source = selector
        .replace(/:where\([^)]*\)/g, '')
        .replace(/:(is|not|has)\(/g, ' ')
        .replace(/\)/g, ' ')
        .replace(/\[[^\]]*\]/g, ' .attr ');
    const ids = (source.match(/#[\w-]+/g) ?? []).length;
    const classes = (source.match(/\.[\w-]+|:(?!:)[\w-]+/g) ?? []).length;
    const types = (source.match(/(^|[\s>+~])[a-zA-Z][\w-]*|::[\w-]+/g) ?? []).length;
    return [ids, classes, types];
}

const PART_ATTRIBUTE = 'data-l5-carried-part';

/**
 * One id's worth of specificity on every copied rule, so the graphic's own rules for the same
 * classes don't restyle the copy — only a rule meant to, and written more specifically, does.
 * Inline styles, which is where the timeline animates, still outrank it.
 */
const COPIED_RULE_WEIGHT = ':not(#l5-carried)';

/**
 * The rules that drew each element of the carried subtree in the graphic before, re-targeted at
 * those very elements through a key on each. Matching happened where the element was laid out, so a
 * rule that needed an ancestor outside the copy (`.chart .bar`) still applies once it is somewhere
 * else, and a rule for the copy's own root is kept rather than falling outside a scope. Emitted in
 * the order they won in there, since re-targeting flattens their specificity.
 */
function keyedRules(originals: Element[], clones: Element[], prefix: string): string[] {
    const css = Array.from(originals[0].ownerDocument.querySelectorAll('style'))
        .map((style) => style.textContent ?? '')
        .join('\n');
    const keys = new Map<number, string>();
    const keyOf = (index: number): string => {
        const existing = keys.get(index);
        if (existing) {
            return existing;
        }
        const key = `${prefix}-${keys.size + 1}`;
        keys.set(index, key);
        clones[index].setAttribute(PART_ATTRIBUTE, key);
        return key;
    };
    const matched: {
        weight: [number, number, number];
        order: number;
        targets: number[];
        pseudo: string;
        body: string;
    }[] = [];
    flatRules(css).forEach(({ selector, body }, order) => {
        for (const part of selectorParts(selector)) {
            const pseudo = part.match(PSEUDO_ELEMENT)?.[0].trim() ?? '';
            const base = pseudo ? part.slice(0, part.length - pseudo.length).trim() : part;
            const targets = originals
                .map((element, index) => {
                    try {
                        return element.matches(base) ? index : -1;
                    } catch {
                        return -1;
                    }
                })
                .filter((index) => index >= 0);
            if (targets.length) {
                matched.push({ weight: specificity(part), order, targets, pseudo, body });
            }
        }
    });
    matched.sort(
        (a, b) =>
            a.weight[0] - b.weight[0] ||
            a.weight[1] - b.weight[1] ||
            a.weight[2] - b.weight[2] ||
            a.order - b.order,
    );
    return matched.map(({ targets, pseudo, body }) => {
        const selector = targets
            .map((index) => `[${PART_ATTRIBUTE}="${keyOf(index)}"]${COPIED_RULE_WEIGHT}${pseudo}`)
            .join(', ');
        return `${selector} { ${body} }`;
    });
}

/**
 * The type the copy's root inherited from wherever it sat. Its descendants inherit from the root, so
 * pinning these on it is what keeps the copy looking the same outside the container that set
 * them.
 */
const INHERITED_TYPE = [
    'color',
    'font-family',
    'font-size',
    'font-style',
    'font-weight',
    'font-stretch',
    'font-variant-numeric',
    'line-height',
    'letter-spacing',
    'word-spacing',
    'text-align',
    'text-transform',
    'text-indent',
    'white-space',
    'direction',
];

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

/**
 * The element's untransformed size. The rendered box has every scale on the way down folded into it,
 * a parent's included, and the copy is placed at the canvas root where none of those parents are —
 * so the scale to reapply is the rendered size over this one.
 */
function layoutSize(element: Element, rect: Rect): { width: number; height: number } {
    const box = element as HTMLElement;
    const width = box.offsetWidth || element.clientWidth || rect.width;
    const height = box.offsetHeight || element.clientHeight || rect.height;
    return { width, height };
}

/**
 * An ancestor outside the element that its absolutely placed parts are laid out against, when that
 * ancestor's box starts somewhere else. The copy's root is positioned, so in the copy those parts are
 * laid out against it instead, and move by the difference. Where the two boxes start at the same
 * point, as a static wrapper first in a full-canvas stage does, the copy lands exactly.
 */
function partPlacedOutside(element: Element): string | null {
    const view = element.ownerDocument.defaultView;
    if (!view) {
        return null;
    }
    // An unset position computes to its initial value, static, which some engines report as ''.
    const positioned = (box: Element): boolean =>
        !['', 'static'].includes(view.getComputedStyle(box).position);
    if (positioned(element)) {
        return null;
    }
    const start = element.getBoundingClientRect();
    for (const part of Array.from(element.querySelectorAll('*'))) {
        const { position } = view.getComputedStyle(part);
        if (position !== 'absolute' && position !== 'fixed') {
            continue;
        }
        // offsetParent falls back to <body> when no ancestor is positioned, but the box they are
        // then placed against is the canvas.
        const offsetParent = position === 'absolute' ? (part as HTMLElement).offsetParent : null;
        const container = offsetParent && positioned(offsetParent) ? offsetParent : null;
        if (container && element.contains(container)) {
            continue;
        }
        // Absolute parts are placed against the container's padding box, inside its border.
        const origin = container
            ? {
                  x: container.getBoundingClientRect().left + container.clientLeft,
                  y: container.getBoundingClientRect().top + container.clientTop,
              }
            : { x: 0, y: 0 };
        if (Math.abs(start.left - origin.x) > 0.5 || Math.abs(start.top - origin.y) > 0.5) {
            return container
                ? `<${container.localName}${container.id ? `#${container.id}` : ''}>`
                : 'the canvas';
        }
    }
    return null;
}

/**
 * The element as it stands, re-placed in canvas pixels. Its descendants keep the inline state the
 * timeline left on them (a stroke's dash offset, a counter's text), which is exactly the part a
 * redraw gets wrong; only the root's own placement is rewritten, since in the next graphic it no
 * longer sits in the container it was laid out in.
 */
function carriedCopy(element: Element, rect: Rect): CarriedCopy | CopyRefusal {
    if (element.namespaceURI === SVG_NAMESPACE && element.localName !== 'svg') {
        return {
            refused: 'is part of an SVG drawing, which draws nothing outside its <svg>',
            fix: 'Mark the whole <svg> instead',
        };
    }
    const style = element.ownerDocument.defaultView?.getComputedStyle(element);
    const rotated =
        !!style?.rotate && style.rotate !== 'none' && !/^0(deg|rad|turn)?$/.test(style.rotate);
    if (!pureScale(style?.transform ?? 'none') || rotated) {
        return {
            refused: 'is rotated or skewed, which a box and a scale cannot reproduce',
            fix: 'Mark an unrotated wrapper around it instead, and turn what is inside',
        };
    }
    const outside = partPlacedOutside(element);
    if (outside) {
        return {
            refused: `has parts placed against ${outside}, which a copy of it leaves behind`,
            fix:
                'Mark the element they are positioned against instead, or give this one ' +
                'position:absolute so they are placed against it',
        };
    }
    const size = layoutSize(element, rect);
    const scale = {
        x: size.width > 0 ? rect.width / size.width : 1,
        y: size.height > 0 ? rect.height / size.height : 1,
    };
    const originals = [element, ...Array.from(element.querySelectorAll('*'))];
    const root = element.cloneNode(true) as Element;
    const clones = [root, ...Array.from(root.querySelectorAll('*'))];
    for (const clone of clones) {
        // Set afresh by this copy; ones left from a copy further back would collide with its keys.
        clone.removeAttribute(PART_ATTRIBUTE);
        clone.removeAttribute('data-l5-carried');
        clone.removeAttribute(COPY_SUM);
    }
    const prefix = carryIdsOf(element.getAttribute('data-l5-carry'))[0] ?? 'carry';
    const rules = keyedRules(originals, clones, prefix);
    root.querySelectorAll('script').forEach((script) => script.remove());

    const kept = (root.getAttribute('style') ?? '')
        .split(';')
        .filter(
            (declaration) =>
                declaration.trim() &&
                !/^\s*(position|inset|left|top|right|bottom|width|height|margin|transform|transform-origin|translate|scale|rotate|box-sizing)\s*:/i.test(
                    declaration,
                ),
        );
    const scaled = Math.abs(scale.x - 1) > 1e-3 || Math.abs(scale.y - 1) > 1e-3;
    // The element's own translation stays a transform, so the next graphic's x and y continue from
    // the values this one's timeline left: folded into left/top, its x would restart at 0 and a
    // graphic that goes on tweening it by the same values would move it twice.
    const translation = scaled ? { x: 0, y: 0 } : translationOf(style?.transform ?? 'none');
    const moved = translation.x !== 0 || translation.y !== 0;
    // A stylesheet's own rotate or scale is already folded into where the element ends up on the
    // canvas, so letting a copied rule apply it again would move it twice.
    const placement = [
        'position:absolute',
        `left:${round(rect.x - translation.x)}px`,
        `top:${round(rect.y - translation.y)}px`,
        `width:${round(size.width)}px`,
        `height:${round(size.height)}px`,
        'margin:0',
        'box-sizing:border-box',
        scaled
            ? `transform:scale(${scale.x},${scale.y})`
            : moved
              ? `transform:translate(${round(translation.x)}px,${round(translation.y)}px)`
              : 'transform:none',
        'transform-origin:0 0',
        'translate:none',
        'rotate:none',
        'scale:none',
    ];
    const inherited = style
        ? INHERITED_TYPE.map((name) => [name, style.getPropertyValue(name)] as const)
              .filter(([, value]) => value)
              .map(([name, value]) => `${name}:${value}`)
        : [];
    root.setAttribute(
        'style',
        [...placement, ...inherited, ...kept.map((part) => part.trim())].join(';'),
    );
    return { element: root.outerHTML, rules };
}

function graphicOf(scene: Scene | undefined): HtmlAsset | null {
    const asset = scene?.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0] as HtmlAsset | undefined;
    return asset?.html ? asset : null;
}

/**
 * A scene's declared handoffs split by whether their two scenes still play one straight after the
 * other. Each is stored against the scenes it was declared between, so adding, moving or removing a
 * scene leaves it naming a seam that no longer exists — and enforcing it then sets the copy, which
 * reads the scene actually before, against a check that reads the one declared.
 */
export function seamsAsPlayed(
    luminary: Luminary,
    declared: { incoming: SceneHandoff[]; outgoing: SceneHandoff[] },
): { incoming: SceneHandoff[]; outgoing: SceneHandoff[]; moved: SceneHandoff[] } {
    const scenes = luminary.scenes;
    const stillMeet = ({ fromSceneId, toSceneId }: SceneHandoff): boolean => {
        const index = scenes.findIndex(({ id }) => id === fromSceneId);
        return index >= 0 && scenes[index + 1]?.id === toSceneId;
    };
    return {
        incoming: declared.incoming.filter(stillMeet),
        outgoing: declared.outgoing.filter(stillMeet),
        moved: [...declared.incoming, ...declared.outgoing].filter(
            (handoff) => !stillMeet(handoff),
        ),
    };
}

/**
 * Planned handoffs whose seam this scene now sits in: declared across a cut between two scenes that
 * now have this one, and maybe others, between them.
 * This scene is not an end of any of them, so nothing else it is told mentions them.
 */
export function seamsRunningThrough(
    luminary: Luminary,
    scene: Scene,
    handoffs: readonly SceneHandoff[],
): SceneHandoff[] {
    const scenes = luminary.scenes;
    const index = scenes.indexOf(scene);
    const at = (sceneId: string): number => scenes.findIndex(({ id }) => id === sceneId);
    const { moved } = seamsAsPlayed(luminary, { incoming: [...handoffs], outgoing: [] });
    // Both ends still there, on either side of this scene: a scene removed from a seam leaves its
    // neighbours outside it, and they were never part of it.
    return moved.filter(({ fromSceneId, toSceneId }) => {
        const from = at(fromSceneId);
        const to = at(toSceneId);
        return from >= 0 && to >= 0 && from < index && index < to;
    });
}

/** What a handoff that no longer names a real seam is, and what to do about it. */
export function movedSeamNote(luminary: Luminary, handoff: SceneHandoff): string {
    const name = (sceneId: string): string =>
        luminary.scenes.some(({ id }) => id === sceneId)
            ? `scene ${sceneDisplayId(luminary, sceneId)}`
            : 'a scene that has since been removed';
    return (
        `Handoff "${handoff.id}" was planned from ${name(handoff.fromSceneId)} into ` +
        `${name(handoff.toSceneId)}, but those no longer play one straight after the other — a ` +
        `scene was added, moved or removed between them — so nothing checks it now. Declare it ` +
        `again with ${TOOL_NAMES.writeDesignSystem} for the seam it belongs on, or drop it.`
    );
}

/**
 * Whether the viewer sees the last frame of `from` and then, with nothing between, the first of `to`.
 * A scene's transition is the one into it, so the seam's is `to`'s.
 */
export function isHardCut(from: Scene, to: Scene): boolean {
    return (
        from.nextScene === to && !hasAppliedTransition(to) && !hasAppliedMatchCutWithNextScene(from)
    );
}

async function closingAndOpening(
    asset: HtmlAsset,
    wantOpening: boolean,
    wantClosing: boolean,
    withCopy = false,
): Promise<{ opening: Map<string, CarryPose>; closing: Map<string, CarryPose> } | null> {
    const progresses = [...(wantOpening ? [0] : []), ...(wantClosing ? [1] : [])];
    const measured = progresses.length
        ? await measureCarries(asset, progresses, { withCopy })
        : new Map();
    if (!measured) {
        return null;
    }
    return { opening: measured.get(0) ?? new Map(), closing: measured.get(1) ?? new Map() };
}

function finding(ruleId: string, severity: LintFinding['severity'], message: string): LintFinding {
    return { ruleId, severity, message };
}

const CHANGED_PARTS_MAX = 3;

/**
 * Properties that change nothing on the frame by themselves — the set-up before a tween, such as the
 * origin a carried rail grows from. Setting them on a copy at its first frame is how it gets extended,
 * which is what a graphic opening on it is asked to do.
 */
const SETUP_ONLY_PROPERTIES =
    /^(transform-origin|will-change|pointer-events|cursor|user-select|transition(-.+)?)$/;
const CHANGED_PROPERTIES_MAX = 2;

function inlineStyle(element: Element): Map<string, string> {
    const style = (element as HTMLElement).style;
    const declarations = new Map<string, string>();
    if (!style) {
        return declarations;
    }
    for (let index = 0; index < style.length; index++) {
        declarations.set(style[index], style.getPropertyValue(style[index]).trim());
    }
    return declarations;
}

function copyIn(root: ParentNode, id: string): Element | undefined {
    return Array.from(root.querySelectorAll('[data-l5-carried]')).find(
        (element) => element.getAttribute('data-l5-carried') === id,
    );
}

const IDENTITY_TRANSFORM =
    /^matrix\(1, 0, 0, 1, 0, 0\)$|^matrix3d\(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1\)$/;

/**
 * A computed value as it renders: an identity transform moves nothing, so it reads as none, as does
 * a transform reported empty where it was never set.
 */
function asRendered(name: string, value: string): string {
    const trimmed = value.trim();
    return name === 'transform' && (trimmed === '' || IDENTITY_TRANSFORM.test(trimmed))
        ? 'none'
        : value;
}

/**
 * Whether two inline values for a property render differently on the element, so a write that only
 * restates what the copy already shows — an opacity of 1 where none was set — is not reported.
 */
function changesComputed(
    element: Element,
    name: string,
    before: string | undefined,
    after: string | undefined,
): boolean {
    const view = element.ownerDocument.defaultView;
    const style = (element as HTMLElement).style;
    if (!view || !style) {
        return true;
    }
    const current = style.getPropertyValue(name);
    const computedWith = (value: string | undefined): string => {
        if (value) {
            style.setProperty(name, value);
        } else {
            style.removeProperty(name);
        }
        return asRendered(name, view.getComputedStyle(element).getPropertyValue(name));
    };
    const differs = computedWith(before) !== computedWith(after);
    computedWith(current);
    return differs;
}

/**
 * The copy's parts whose inline state differs on this graphic's first frame from how it was copied
 * in — where a set or from at 0 lands. Named part by part, since a pixel region alone leaves the
 * author to guess which of its own writes did it. Parts are paired in document order, which the
 * timeline does not change.
 */
async function copyChangesAtOpen(asset: HtmlAsset, id: string): Promise<string[]> {
    const source = copyIn(new DOMParser().parseFromString(asset.html ?? '', 'text/html'), id);
    if (!source) {
        return [];
    }
    const sourceParts = [source, ...Array.from(source.querySelectorAll('*'))];
    const changes = await asset.introspectComposition(async (composition) => {
        const seek: FrameSource['seek'] = composition.seek;
        const posed = (await seek(0)) ?? composition.document;
        const live = copyIn(posed, id);
        if (!live) {
            return [];
        }
        const liveParts = [live, ...Array.from(live.querySelectorAll('*'))];
        return sourceParts.flatMap((part, index) => {
            const opened = liveParts[index];
            if (!opened) {
                return [];
            }
            const before = inlineStyle(part);
            const after = inlineStyle(opened);
            const properties = [...new Set([...before.keys(), ...after.keys()])]
                .filter(
                    (name) =>
                        !SETUP_ONLY_PROPERTIES.test(name) &&
                        before.get(name) !== after.get(name) &&
                        changesComputed(opened, name, before.get(name), after.get(name)),
                )
                .slice(0, CHANGED_PROPERTIES_MAX)
                .map(
                    (name) =>
                        `${name} ${before.get(name) || 'unset'} → ${after.get(name) || 'unset'}`,
                );
            return properties.length ? [`${labelOf(part)} ${properties.join(', ')}`] : [];
        });
    });
    return (changes ?? []).slice(0, CHANGED_PARTS_MAX);
}

/**
 * Where a carry-in placeholder goes, said the same way by the brief and every error. The copy is
 * placed in canvas pixels, so it needs a full-canvas parent; and it paints in document order, so
 * anything opaque after it — a stage with a background — covers it.
 */
export function placeholderFor(id: string): string {
    return (
        `<div data-l5-carry-in="${id}"></div> directly inside the full-canvas stage, after its ` +
        `background (or last in <body> if there is no stage)`
    );
}

const RESTYLING_RULES_MAX = 3;

/**
 * This graphic's own CSS rules that reach parts of the copy it opened on — what restyles a copy when
 * nothing inline changed. Rules the graphic before has too (the design system's, say) drew the copy
 * there as well, so only the ones it does not are named.
 */
function restylingRules(
    html: string | undefined,
    before: string | undefined,
    id: string,
): string[] {
    const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
    const copy = copyIn(doc, id);
    if (!copy) {
        return [];
    }
    const parts = [copy, ...Array.from(copy.querySelectorAll('*'))];
    const css = Array.from(doc.querySelectorAll('style:not([data-l5-carried])'))
        .map((style) => style.textContent ?? '')
        .join('\n');
    const theirs = new Set(
        flatRules(
            Array.from(
                new DOMParser()
                    .parseFromString(before ?? '', 'text/html')
                    .querySelectorAll('style'),
            )
                .map((style) => style.textContent ?? '')
                .join('\n'),
        ).flatMap(({ selector }) => selectorParts(selector)),
    );
    const named = new Set<string>();
    for (const { selector } of flatRules(css)) {
        for (const part of selectorParts(selector).filter((each) => !theirs.has(each))) {
            const base = part.replace(PSEUDO_ELEMENT, '').trim();
            const reaches = parts.some((element) => {
                try {
                    return element.matches(base);
                } catch {
                    return false;
                }
            });
            if (reaches) {
                named.add(part);
            }
        }
    }
    return [...named].slice(0, RESTYLING_RULES_MAX);
}

/** How an element is named in a message: its id, else its first class, else its tag. */
function labelOf(element: Element): string {
    const className = element.getAttribute('class')?.trim().split(/\s+/)[0];
    return element.id ? `#${element.id}` : className ? `.${className}` : `<${element.localName}>`;
}

/**
 * A copy arrives drawn exactly as the graphic before ends it, so one that shows nothing on the first
 * frame is covered: by what comes after it in the page, which paints over it. Naming those, since
 * the generic advice — mark what is actually carried — is wrong for a copy.
 */
function coveredCopyMessage(html: string | undefined, id: string): string {
    const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
    const copy = copyIn(doc, id);
    const after: string[] = [];
    for (
        let node: Element | null | undefined = copy;
        node && node !== doc.body;
        node = node.parentElement
    ) {
        for (let sibling = node.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
            if (!['script', 'style'].includes(sibling.localName)) {
                after.push(labelOf(sibling));
            }
        }
    }
    const covering = after.length
        ? `${after.slice(0, 2).join(' and ')} ${after.length > 1 ? 'come' : 'comes'} after it and ` +
          `${after.length > 1 ? 'paint' : 'paints'} over it, or a rule of this graphic hides it`
        : 'a rule of this graphic hides it';
    return (
        `The copy of "${id}" is in place, but nothing of it shows on this graphic's first frame: ` +
        `${covering}. Put ${placeholderFor(id)}, and add your own layers after it only where they ` +
        `should sit over it.`
    );
}

function restyledCopyCause(rules: string[]): string {
    return rules.length
        ? ` It was copied in exactly as it ends there, and rules of this graphic's own reach parts ` +
              `of it: ${rules.map((rule) => `\`${rule}\``).join(', ')}. Keep those to your own ` +
              `elements, so the copy keeps the look it arrived with.`
        : ` It was copied in exactly as it ends there, so this graphic changes it on its first ` +
              `frame — a rule of its own that restyles it. Leave the copy as it arrives and animate ` +
              `on from there.`;
}

/**
 * Why a copy opens away from the canvas pixels it was copied in at: something between it and <body>
 * places it, or this graphic moves it on its first frame. Naming the elements it sits in, since the
 * author can't see which of them is offset.
 */
function movedCopyCause(html: string | undefined, id: string): string {
    const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
    const copy = Array.from(doc.querySelectorAll('[data-l5-carried]')).find(
        (element) => element.getAttribute('data-l5-carried') === id,
    );
    const containers: string[] = [];
    for (
        let parent = copy?.parentElement;
        parent && parent !== doc.body;
        parent = parent.parentElement
    ) {
        containers.unshift(labelOf(parent));
    }
    const inside = containers.length
        ? `it sits inside ${containers.join(' > ')}, and one of them offsets it (left/top, padding, ` +
          `a transform, or a tween on it), or `
        : '';
    return (
        ` It was copied in at the very canvas pixels it ended on, so something here moves it before ` +
        `its first frame: ${inside}this graphic sets its x or y at 0. Put ${placeholderFor(id)}, ` +
        `and leave the copy's position as it arrives.`
    );
}

/**
 * Findings for the seams on either side of the graphic just written. The seam into this graphic is
 * the author's to fix now, so a broken one is an error; the seam out of it into a graphic already
 * built is fixed in that other scene, which this turn is not working on, so it is a warning that
 * names it.
 */
export async function seamFindings(
    luminary: Luminary,
    scene: Scene,
    asset: HtmlAsset,
    planned: { incoming: SceneHandoff[]; outgoing: SceneHandoff[] },
): Promise<LintFinding[]> {
    const { moved, ...declared } = seamsAsPlayed(luminary, planned);
    const marked = markedCarryIds(asset.html);
    const copiedIn = new Set(
        Array.from(
            (asset.html ?? '').matchAll(/data-l5-carried\s*=\s*["']([^"']+)["']/g),
            (match) => match[1].trim(),
        ),
    );
    // Measured as one element, so a second mark leaves the seam comparing whichever came last.
    const findings: LintFinding[] = carryIdsMarkedTwice(asset.html).map((id) =>
        finding(
            'carry-marked-twice',
            'error',
            copiedIn.has(id)
                ? `The copy of "${id}" carried in already carries data-l5-carry="${id}", and so ` +
                      `does an element of yours beside it. To hand both on, put them in one ` +
                      `full-canvas wrapper and mark that; otherwise take the mark off yours.`
                : `data-l5-carry="${id}" marks more than one element here, and a carry is one ` +
                      `element. Put the parts in one wrapper and mark that instead.`,
        ),
    );
    findings.push(
        ...moved.map((handoff) =>
            finding('carry-seam-moved', 'warning', movedSeamNote(luminary, handoff)),
        ),
    );
    const nameOf = (other: Scene): string => sceneDisplayId(luminary, other.id);

    for (const handoff of declared.incoming) {
        if (copiedIn.has(handoff.id)) {
            continue;
        }
        // The plan calls for this element to continue, and a second drawing of it is what every
        // seam that jumped was: so where a copy can be made, the copy is the only way in.
        const { copyable, why } = copyAvailability(await closingPoseOf(luminary, handoff));
        if (copyable) {
            findings.push(
                finding(
                    'carry-not-copied',
                    'error',
                    `This graphic opens on "${handoff.id}" (${handoff.element}), which is copied in ` +
                        `from the graphic before, not drawn here. Put ${placeholderFor(handoff.id)} in ` +
                        `place of your own version, then animate the copy by its ids and build ` +
                        `around it.`,
                ),
            );
        } else if (!marked.has(handoff.id)) {
            findings.push(
                finding(
                    'carry-not-marked',
                    'error',
                    why
                        ? `"${handoff.id}" ${why}, so it can't be copied in: build it yourself, ` +
                              `marked data-l5-carry="${handoff.id}", opening exactly where the ` +
                              `graphic before leaves it.`
                        : `This graphic opens on "${handoff.id}" (${handoff.element}), but the ` +
                              `graphic before isn't built or doesn't mark it yet. Build that one ` +
                              `first, then put ${placeholderFor(handoff.id)} here to copy it in.`,
                ),
            );
        }
    }
    for (const handoff of declared.outgoing) {
        if (!marked.has(handoff.id)) {
            findings.push(
                finding(
                    'carry-not-marked',
                    'error',
                    `The design system plans handoff "${handoff.id}" (${handoff.element}), which ` +
                        `this graphic leaves on screen for the next one, but nothing here is marked ` +
                        `data-l5-carry="${handoff.id}". Mark the element that carries it.`,
                ),
            );
        }
    }

    const scenes = luminary.scenes;
    const index = scenes.indexOf(scene);
    const previous = index > 0 ? scenes[index - 1] : undefined;
    const next = index >= 0 ? scenes[index + 1] : undefined;
    const previousGraphic = graphicOf(previous);
    const nextGraphic = graphicOf(next);
    const previousIds = markedCarryIds(previousGraphic?.html);
    const nextIds = markedCarryIds(nextGraphic?.html);
    const incomingIds = [...marked].filter(
        (id) => previousIds.has(id) || declared.incoming.some((handoff) => handoff.id === id),
    );
    const outgoingIds = [...marked].filter(
        (id) => nextIds.has(id) || declared.outgoing.some((handoff) => handoff.id === id),
    );
    if (incomingIds.length === 0 && outgoingIds.length === 0) {
        return findings;
    }

    const handedOn = declared.outgoing.filter((handoff) => marked.has(handoff.id));
    const own = await closingAndOpening(
        asset,
        incomingIds.length > 0,
        outgoingIds.length > 0,
        handedOn.length > 0,
    );
    if (!own) {
        return findings;
    }
    // The next graphic opens on a copy of what this one marks, so a mark that can't be copied
    // forces it to draw its own, which is the redraw a declared handoff exists to avoid.
    for (const handoff of handedOn) {
        const copy = own.closing.get(handoff.id)?.copy;
        if (copy && 'refused' in copy) {
            findings.push(
                finding(
                    'carry-not-copyable',
                    'error',
                    `The element marked data-l5-carry="${handoff.id}" ${copy.refused}, so the next ` +
                        `graphic can't open on a copy of it. ${copy.fix}.`,
                ),
            );
        }
    }

    const brokenCut = (
        from: Scene | undefined,
        to: Scene | undefined,
        id: string,
    ): string | null => {
        if (!from || !to) {
            return `"${id}" is carried across a seam that has no graphic on the other side.`;
        }
        if (!isHardCut(from, to)) {
            return (
                `"${id}" is carried from scene ${nameOf(from)} into scene ${nameOf(to)}, but the two ` +
                `don't meet at a straight cut — a transition, a match cut or a scene between them ` +
                `plays over the seam, so no pose can line up across it. Remove what sits on the ` +
                `seam, or drop the carry.`
            );
        }
        return null;
    };

    const incomingIdsToCompare = incomingIds.filter((id) => {
        const broken = brokenCut(previous, scene, id);
        if (broken) {
            findings.push(finding('carry-seam-broken', 'warning', broken));
        }
        return !broken && previousGraphic && previousIds.has(id);
    });
    const previousPoses =
        previousGraphic && incomingIdsToCompare.length
            ? await closingAndOpening(previousGraphic, false, true)
            : null;
    const blank = (id: string, when: string): LintFinding =>
        finding(
            'carry-draws-nothing',
            'error',
            `The element marked data-l5-carry="${id}" draws nothing on ${when} — an empty box ` +
                `matches its twin across the cut while what the viewer sees jumps. Mark the element ` +
                `that is actually carried.`,
        );
    // A frame with nothing on it says nothing about the carry, and sending the author to its
    // markup is how a carry that was fine got rewritten until it was dropped.
    const blankFrame = (when: string): LintFinding =>
        finding(
            'carry-draws-nothing',
            'error',
            `Nothing at all renders on ${when}, so this is the composition, not the carry: give ` +
                `html and body an explicit width and height the size of the canvas. A page whose ` +
                `content is all absolutely placed has no size of its own, and is captured as nothing.`,
        );

    for (const id of incomingIds) {
        const wrapped = own.opening.get(id);
        const opening = wrapped?.opensAs ?? wrapped;
        if (!opening || opening.opacity < VISIBLE_OPACITY) {
            findings.push(
                finding(
                    'carry-not-on-screen-at-open',
                    'error',
                    `"${id}" is carried in from the graphic before, so it has to be on screen, fully ` +
                        `opaque, on this graphic's first frame — the viewer already sees it. Set its ` +
                        `pose in the CSS or with a set at 0, not with an entrance.`,
                ),
            );
            continue;
        }
        // After the opacity check: an element faded out rasterizes as nothing too, and being told
        // it is not on screen is the more precise of the two.
        if (opening.blank) {
            findings.push(
                opening.frameBlank
                    ? blankFrame("this graphic's first frame")
                    : copiedIn.has(id)
                      ? finding('carry-draws-nothing', 'error', coveredCopyMessage(asset.html, id))
                      : blank(id, "this graphic's first frame"),
            );
            continue;
        }
        const closing = previousPoses?.closing.get(id);
        if (!incomingIdsToCompare.includes(id) || !closing || closing.opacity < VISIBLE_OPACITY) {
            continue;
        }
        const differences = seamDifferences(closing, opening);
        // Read off the copy itself rather than the pixels: a few small parts switched off or
        // recoloured on the first frame are well under any share of a large carried element.
        const changed = copiedIn.has(id) ? await copyChangesAtOpen(asset, id) : [];
        if (differences.length === 0 && changed.length) {
            findings.push(
                finding(
                    'carry-seam-mismatch',
                    'error',
                    `"${id}" changes at the cut from scene ${nameOf(previous!)}: it was copied in ` +
                        `exactly as it ends there, and this graphic changes it on its first frame: ` +
                        `${changed.join('; ')}. Leave those as they arrive and animate on from there.`,
                ),
            );
            continue;
        }
        if (differences.length) {
            // A copied-in element is placed at the very canvas pixels it ended on, so a copy that
            // opens elsewhere is sitting inside something that moves it.
            const moved =
                Math.abs(opening.rect.x - closing.rect.x) > POSITION_TOLERANCE_PX ||
                Math.abs(opening.rect.y - closing.rect.y) > POSITION_TOLERANCE_PX;
            const cause = !copiedIn.has(id)
                ? ` That graphic ends with it at ${describePose(closing)} — open on exactly that ` +
                  `pose, and move it only once this graphic's first phase begins.`
                : changed.length
                  ? ` It was copied in exactly as it ends there, and this graphic changes it on its ` +
                    `first frame: ${changed.join('; ')}. Leave those as they arrive and animate ` +
                    `on from there.`
                  : moved
                    ? movedCopyCause(asset.html, id)
                    : restyledCopyCause(restylingRules(asset.html, previousGraphic?.html, id));
            findings.push(
                finding(
                    'carry-seam-mismatch',
                    'error',
                    `"${id}" jumps at the cut from scene ${nameOf(previous!)}: ` +
                        `${differences.join('; ')}.${cause}`,
                ),
            );
        }
    }

    const outgoingToCompare = outgoingIds.filter((id) => {
        const broken = brokenCut(scene, next, id);
        if (broken) {
            findings.push(finding('carry-seam-broken', 'warning', broken));
        }
        return !broken && nextGraphic && nextIds.has(id);
    });
    const nextPoses =
        nextGraphic && outgoingToCompare.length
            ? await closingAndOpening(nextGraphic, true, false)
            : null;
    for (const id of outgoingIds) {
        const closing = own.closing.get(id);
        if (!closing || closing.opacity < VISIBLE_OPACITY) {
            findings.push(
                finding(
                    'carry-not-on-screen-at-close',
                    'error',
                    `"${id}" is handed on to the next graphic, so it has to stay on screen, fully ` +
                        `opaque, through this graphic's last frame. Don't exit or fade it.`,
                ),
            );
            continue;
        }
        // After the opacity check: an element faded out rasterizes as nothing too, and being told
        // it is not on screen is the more precise of the two.
        if (closing.blank) {
            findings.push(
                closing.frameBlank
                    ? blankFrame("this graphic's last frame")
                    : blank(id, "this graphic's last frame"),
            );
            continue;
        }
        const nextOpening = nextPoses?.opening.get(id);
        const opening = nextOpening?.opensAs ?? nextOpening;
        if (!outgoingToCompare.includes(id) || !opening) {
            continue;
        }
        const differences = seamDifferences(closing, opening);
        if (differences.length) {
            findings.push(
                finding(
                    'carry-seam-mismatch-next',
                    'warning',
                    `"${id}" now ends differently from how scene ${nameOf(next!)} opens on it ` +
                        `(${differences.join('; ')}). This graphic's ending is the one that ` +
                        `stands: re-write that graphic with its <div data-l5-carry-in="${id}"></div> ` +
                        `placeholder, so it copies the new ending in.`,
                ),
            );
        }
    }
    return findings;
}

/** Where the graphic a handoff leaves ends with the carried element, when it is built and marks it. */
export async function closingPoseOf(
    luminary: Luminary,
    handoff: SceneHandoff,
): Promise<CarryPose | null> {
    const asset = graphicOf(luminary.getSceneById(handoff.fromSceneId));
    if (!asset || !markedCarryIds(asset.html).has(handoff.id)) {
        return null;
    }
    const measured = await measureCarries(asset, [1], { withCopy: true });
    return measured?.get(1)?.get(handoff.id) ?? null;
}

/**
 * Whether the element a handoff carries can be copied in: the graphic before is built, marks it, and
 * it is not something a copy cannot reproduce. `why` says why not, for a rebuild to be asked for.
 */
export function copyAvailability(pose: CarryPose | null): { copyable: boolean; why?: string } {
    if (!pose) {
        return { copyable: false };
    }
    if (pose.copy && 'refused' in pose.copy) {
        return { copyable: false, why: pose.copy.refused };
    }
    return { copyable: Boolean(pose.copy) };
}

/** An empty element standing where the graphic before's carried element is to be copied in. */
const CARRY_IN_PLACEHOLDER =
    /<([a-zA-Z][\w-]*)\b[^>]*?\bdata-l5-carry-in\s*=\s*["']([^"']+)["'][^>]*?(?:\/>|>\s*<\/\1\s*>)/g;

export interface SplicedCarries {
    html: string;
    /** The ids copied in, and the scene they came from, for the tool result. */
    spliced: { ids: string[]; fromScene: string } | null;
}

function idsIn(markup: string): string[] {
    return Array.from(markup.matchAll(/\sid\s*=\s*["']([^"']+)["']/g), (match) => match[1]);
}

/**
 * Where the rules drawing the copies go: after the graphic's own styles, so at equal weight the
 * copied rule is the later one and the copy keeps its look. The keys they target exist only on the
 * copies, so nothing else in the graphic is touched.
 */
function withCarriedRules(html: string, rules: string[]): string {
    if (rules.length === 0) {
        return html;
    }
    const block = `<style data-l5-carried>\n${rules.join('\n')}\n</style>`;
    const headEnd = html.search(/<\/head\s*>/i);
    if (headEnd >= 0) {
        return `${html.slice(0, headEnd)}${block}\n${html.slice(headEnd)}`;
    }
    const lastStyleEnd = html.lastIndexOf('</style>');
    if (lastStyleEnd >= 0) {
        const at = lastStyleEnd + '</style>'.length;
        return `${html.slice(0, at)}\n${block}${html.slice(at)}`;
    }
    return `${block}\n${html}`;
}

const CARRY_VALUE = /\bdata-l5-carry\s*=\s*["']([^"']+)["']/;

/**
 * The copy's root, marked with the id it was carried in by — what the seam check reads to tell a copy
 * from a rebuild — and with any ids the placeholder hands it on under, since the copy replaces the placeholder whole and a
 * graphic that passes the element on under a new id has nowhere else to say so.
 */
function markedCopy(element: string, id: string, placeholder: string): string {
    const openingTag = element.match(/^<[^>]*>/)?.[0] ?? '';
    const ids = [
        ...new Set([
            ...carryIdsOf(openingTag.match(CARRY_VALUE)?.[1]),
            ...carryIdsOf(placeholder.match(CARRY_VALUE)?.[1]),
        ]),
    ];
    const retagged = openingTag
        .replace(/\s+data-l5-carry\s*=\s*["'][^"']*["']/, '')
        .replace(
            /^<([a-zA-Z][\w-]*)/,
            `<$1 data-l5-carried="${id}"${ids.length ? ` data-l5-carry="${ids.join(' ')}"` : ''}`,
        );
    return retagged + element.slice(openingTag.length);
}

const COPY_SUM = 'data-l5-carried-sum';

/** FNV-1a: only has to tell a copy the agent has edited from one it hasn't. */
function fingerprint(text: string): string {
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index++) {
        hash = Math.imul(hash ^ text.charCodeAt(index), 0x01000193);
    }
    return (hash >>> 0).toString(16);
}

/** The copy with a fingerprint of itself on its root, so a later refresh can tell it is untouched. */
function stamped(copy: string): string {
    return copy.replace(/^(<[^>]*?)(\/?>)/, `$1 ${COPY_SUM}="${fingerprint(copy)}"$2`);
}

const VOID_ELEMENTS = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'source',
    'track',
    'wbr',
]);

/** Where the element whose opening tag starts at `start` ends in the source, or -1. */
function elementEnd(html: string, start: number): number {
    const tag = /<(\/?)([a-zA-Z][\w-]*)\b[^>]*?(\/?)>/g;
    tag.lastIndex = start;
    let depth = 0;
    for (let match = tag.exec(html); match; match = tag.exec(html)) {
        const [whole, closing, name, selfClosing] = match;
        if (closing) {
            depth--;
        } else if (!selfClosing && !VOID_ELEMENTS.has(name.toLowerCase())) {
            depth++;
        }
        if (depth === 0) {
            return match.index + whole.length;
        }
    }
    return -1;
}

/** Whether the element with this id in the source holds the offset `at`. */
function enclosedBy(html: string, id: string, at: number): boolean {
    const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const opening = new RegExp(`<[a-zA-Z][\\w-]*\\b[^>]*\\sid\\s*=\\s*["']${escaped}["'][^>]*>`);
    const match = opening.exec(html);
    if (!match) {
        return false;
    }
    const start = match.index;
    return start < at && at < elementEnd(html, start);
}

interface CopyInSource {
    id: string;
    start: number;
    end: number;
    /** The ids the copy hands on, which the placeholder it came from carried. */
    handsOn: string[];
    untouched: boolean;
}

const COPY_SUM_ATTRIBUTE = new RegExp(`\\s${COPY_SUM}\\s*=\\s*["']([^"']*)["']`);

function copiesIn(html: string): CopyInSource[] {
    return Array.from(
        html.matchAll(/<[a-zA-Z][\w-]*\b[^>]*\bdata-l5-carried\s*=\s*["']([^"']+)["'][^>]*>/g),
        (match) => {
            const start = match.index ?? 0;
            const end = elementEnd(html, start);
            const text = end < 0 ? '' : html.slice(start, end);
            const sum = match[0].match(COPY_SUM_ATTRIBUTE)?.[1];
            return {
                id: match[1].trim(),
                start,
                end,
                handsOn: carryIdsOf(match[0].match(CARRY_VALUE)?.[1]),
                untouched:
                    end > 0 && !!sum && sum === fingerprint(text.replace(COPY_SUM_ATTRIBUTE, '')),
            };
        },
    );
}

/**
 * Re-copies what the graphics after this one copied in from it, so an edit here reaches the graphic
 * that opens on it instead of leaving that one to be re-written by hand — and on down the chain,
 * since a refreshed copy can change how that graphic ends too. Only a copy no one has edited since
 * it was spliced is refreshed; one the agent has changed is its own, and the seam check still names
 * it. Returns the scenes refreshed.
 */
export async function refreshCopiesAfter(luminary: Luminary, scene: Scene): Promise<string[]> {
    const refreshed: string[] = [];
    const scenes = luminary.scenes;
    for (let index = scenes.indexOf(scene); index >= 0 && index < scenes.length - 1; index++) {
        const giver = graphicOf(scenes[index]);
        const next = scenes[index + 1];
        const receiver = graphicOf(next);
        if (!giver || !receiver?.html || !isHardCut(scenes[index], next)) {
            break;
        }
        const marked = markedCarryIds(giver.html);
        const copies = copiesIn(receiver.html);
        // The rules for every copy share one block, so a graphic is refreshed whole or not at all.
        if (copies.length === 0 || copies.some((copy) => !copy.untouched || !marked.has(copy.id))) {
            break;
        }
        const withPlaceholders = [...copies]
            .sort((a, b) => b.start - a.start)
            .reduce(
                (text, copy) =>
                    `${text.slice(0, copy.start)}<div data-l5-carry-in="${copy.id}"${
                        copy.handsOn.length ? ` data-l5-carry="${copy.handsOn.join(' ')}"` : ''
                    }></div>${text.slice(copy.end)}`,
                receiver.html,
            )
            .replace(/\n?<style data-l5-carried>[\s\S]*?<\/style>/g, '');
        const result = await spliceCarries(luminary, next, withPlaceholders);
        if ('error' in result || result.html === receiver.html) {
            break;
        }
        luminary.batchRun(() => {
            receiver.html = result.html;
        });
        refreshed.push(sceneDisplayId(luminary, next.id));
    }
    return refreshed;
}

/**
 * Replaces each `data-l5-carry-in` placeholder with the element the graphic straight before this one
 * carries, copied from its last frame — so the seam matches by construction, with no redraw to get
 * wrong and no markup passed through the agent. Done on the source text rather than a re-serialized
 * document, because anything else about the composition changing under the agent would break the
 * find-and-replace its next edit is written against.
 *
 * The copy is a snapshot of that graphic as it stands now; `refreshCopiesAfter` brings it up to date
 * when that graphic changes, unless this one has edited the copy since.
 */
export async function spliceCarries(
    luminary: Luminary,
    scene: Scene,
    html: string,
): Promise<SplicedCarries | { error: string }> {
    const placeholders = Array.from(html.matchAll(CARRY_IN_PLACEHOLDER));
    if (placeholders.length === 0) {
        return /data-l5-carry-in/.test(html)
            ? {
                  error:
                      'A data-l5-carry-in placeholder has to be an empty element, e.g. ' +
                      '<div data-l5-carry-in="id"></div>: it is replaced whole, so nothing put inside ' +
                      'it would survive.',
              }
            : { html, spliced: null };
    }
    const ids = placeholders.map((match) => match[2].trim());

    const scenes = luminary.scenes;
    const previous = scenes[scenes.indexOf(scene) - 1];
    const previousGraphic = graphicOf(previous);
    if (!previous || !previousGraphic) {
        return {
            error:
                `This composition carries in ${ids.map((id) => `"${id}"`).join(', ')}, but no built ` +
                `graphic plays straight before it to copy from. Build that graphic first, or draw ` +
                `the element yourself.`,
        };
    }
    const fromScene = sceneDisplayId(luminary, previous.id);
    if (!isHardCut(previous, scene)) {
        return {
            error:
                `Scene ${fromScene} and this one don't meet at a straight cut, so nothing can carry ` +
                `across the seam. Remove what sits on it, or drop the placeholder.`,
        };
    }
    const unmarked = ids.filter((id) => !markedCarryIds(previousGraphic.html).has(id));
    if (unmarked.length) {
        return {
            error: `Scene ${fromScene} marks nothing data-l5-carry=${unmarked
                .map((id) => `"${id}"`)
                .join(', ')}, so there is nothing to copy in. Mark the element there first.`,
        };
    }

    const closing = (await measureCarries(previousGraphic, [1], { withCopy: true }))?.get(1);
    if (!closing) {
        return { error: `Scene ${fromScene} could not be loaded to copy its last frame from.` };
    }
    const copies: CarriedCopy[] = [];
    for (const id of ids) {
        const pose = closing.get(id);
        if (!pose || pose.opacity < VISIBLE_OPACITY) {
            return {
                error:
                    `"${id}" isn't on screen as scene ${fromScene} ends, so there is nothing to copy ` +
                    `in. Keep it there through that graphic's last frame.`,
            };
        }
        if (!pose.copy || 'refused' in pose.copy) {
            const why =
                pose.copy && 'refused' in pose.copy ? pose.copy.refused : 'could not be copied';
            return {
                error:
                    `"${id}" ${why} as scene ${fromScene} ends it, so it can't be copied in. Build ` +
                    `it yourself, opening at ${describePose(pose)}.`,
            };
        }
        copies.push(pose.copy);
    }

    // A graphic handing the element on wraps its copy, and tends to name the wrapper as the graphic
    // before named its own — the copy's root. That id can only mean the wrapper its author wrote, so
    // the copy gives it up; the copy is still found by data-l5-carried.
    const elements = copies.map(({ element }, index) => {
        const openingTag = element.match(/^<[^>]*>/)?.[0] ?? '';
        const [rootId] = idsIn(openingTag);
        return rootId && enclosedBy(html, rootId, placeholders[index].index ?? 0)
            ? openingTag.replace(/\sid\s*=\s*["'][^"']*["']/, '') + element.slice(openingTag.length)
            : element;
    });

    // The copies keep their ids so this graphic can animate them; one already used here would make
    // every selector for it ambiguous.
    const taken = new Set(
        idsIn(placeholders.reduce((text, match) => text.replace(match[0], ''), html)),
    );
    const clashes = [...new Set(elements.flatMap((element) => idsIn(element)))].filter((id) =>
        taken.has(id),
    );
    if (clashes.length) {
        return {
            error: `The element copied in from scene ${fromScene} uses id ${clashes
                .map((id) => `"${id}"`)
                .join(', ')}, which this composition already uses. Rename yours.`,
        };
    }

    // A replacer function, so a `$&` or `$$` in the copied markup is copied rather than expanded.
    const spliced = placeholders.reduce(
        (text, match, index) =>
            text.replace(match[0], () =>
                stamped(markedCopy(elements[index], ids[index], match[0])),
            ),
        html,
    );
    return {
        html: withCarriedRules(
            spliced,
            copies.flatMap(({ rules }) => rules),
        ),
        spliced: { ids, fromScene },
    };
}
